// Shared referrals content — used by both the legacy /account page and the new /dashboard.
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useToast } from '../hooks/use-toast';
import { Loader2, Copy, Check, Gift, Tag, Share2, MessageCircle, Send, Twitter, Mail } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatCard = ({ label, value, testid }) => (
  <div className="bg-white/10 rounded-xl px-4 py-3 border border-white/15" data-testid={testid}>
    <div className="text-2xl font-display font-bold">{value}</div>
    <div className="text-[10px] uppercase tracking-widest text-white/60 mt-0.5">{label}</div>
  </div>
);

const ShareBtn = ({ href, icon: Icon, label, bg, testid }) => (
  <a data-testid={testid} href={href} target="_blank" rel="noopener noreferrer"
    className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-white font-semibold text-xs hover:opacity-90 transition-opacity"
    style={{ backgroundColor: bg }}>
    <Icon className="w-3.5 h-3.5" /> {label}
  </a>
);

const CodeRow = ({ c, onCopy, copiedKey, active }) => (
  <div className={`flex items-center justify-between gap-3 rounded-xl border p-3 ${active ? 'border-emerald-200 bg-emerald-50/60' : 'border-neutral-200 bg-neutral-50 opacity-70'}`} data-testid={`personal-code-${c.code}`}>
    <div className="min-w-0 flex-1">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-mono font-bold text-neutral-900 tracking-wider">{c.code}</span>
        <span className="text-xs font-semibold text-[#8247E5]">{c.discount_pct}% off</span>
        {!active && <span className="text-[10px] uppercase tracking-widest text-neutral-400">Used</span>}
      </div>
      {c.description && <div className="text-[11px] text-neutral-500 truncate mt-0.5">{c.description}</div>}
    </div>
    {active && (
      <Button variant="outline" size="sm" onClick={() => onCopy(c.code, c.code)} className="gap-1.5 h-8" data-testid={`copy-personal-code-${c.code}`}>
        {copiedKey === c.code ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
        {copiedKey === c.code ? 'Copied' : 'Copy'}
      </Button>
    )}
  </div>
);

const ReferralsTab = () => {
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [codeInput, setCodeInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [copiedKey, setCopiedKey] = useState(null);

  const fetchData = async () => {
    setBusy(true);
    try {
      const { data } = await axios.get(`${API}/account/referral`);
      setData(data);
      setCodeInput(data.referral_code || '');
    } catch { toast({ title: 'Failed to load referrals', variant: 'destructive' }); }
    finally { setBusy(false); }
  };
  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, []);

  const saveCode = async () => {
    const code = codeInput.trim().toUpperCase();
    if (!code || code.length < 3) { toast({ title: 'At least 3 characters' }); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/account/referral`, { code });
      toast({ title: 'Referral code saved!' });
      fetchData();
    } catch (e) {
      toast({ title: 'Save failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const copy = (t, key) => {
    navigator.clipboard.writeText(t);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 1500);
    toast({ title: 'Copied' });
  };

  if (busy && !data) return <div className="text-center py-16"><Loader2 className="w-6 h-6 animate-spin mx-auto text-neutral-400" /></div>;
  const link = data?.referral_code ? `${window.location.origin}/?ref=${encodeURIComponent(data.referral_code)}` : null;
  const usableCodes = (data?.personal_codes || []).filter(c => c.used_count < (c.max_uses ?? Infinity) && c.active);
  const usedCodes = (data?.personal_codes || []).filter(c => c.used_count >= (c.max_uses ?? Infinity) || !c.active);

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-br from-violet-600 to-purple-700 rounded-2xl p-6 text-white relative overflow-hidden" data-testid="referral-hero">
        <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full bg-white/10 blur-2xl" />
        <div className="relative">
          <div className="text-[11px] uppercase tracking-widest font-semibold text-white/70 flex items-center gap-1"><Gift className="w-3 h-3" /> Your referral program</div>
          <h3 className="font-display font-bold text-2xl mt-1 mb-3">
            Give 10%, get 10% <span className="text-white/60 font-normal text-base">off service fee</span>
          </h3>
          <p className="text-sm text-white/80 mb-4 leading-relaxed">
            Share your code. When a friend signs up and completes their first paid order, <b>both of you get 10% off</b> your next service fee.
          </p>

          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-widest font-semibold text-white/60 mb-1">Your referral code</div>
              <div className="flex gap-2">
                <Input data-testid="referral-code-input" value={codeInput} onChange={e => setCodeInput(e.target.value.toUpperCase())}
                  placeholder="e.g. YOUR-NAME" className="bg-white/95 text-neutral-900 font-mono tracking-wider h-11" maxLength={32} />
                <Button onClick={saveCode} disabled={saving || !codeInput || codeInput === (data?.referral_code || '')} className="bg-white text-[#8247E5] hover:bg-white/90 h-11 px-4" data-testid="referral-save-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (data?.referral_code ? 'Update' : 'Set code')}
                </Button>
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest font-semibold text-white/60 mb-1">Your referral link</div>
              <div className="flex gap-2">
                <div className="flex-1 h-11 rounded-md bg-white/10 border border-white/20 px-3 flex items-center text-xs font-mono truncate">
                  {link || 'Set a code above to generate your link'}
                </div>
                <Button data-testid="referral-copy-link" disabled={!link} onClick={() => copy(link, 'link')} className="bg-white text-[#8247E5] hover:bg-white/90 h-11 px-3">
                  {copiedKey === 'link' ? <Check className="w-4 h-4" /> : <Share2 className="w-4 h-4" />}
                </Button>
              </div>
            </div>
          </div>

          <div className="mt-5 grid grid-cols-2 md:grid-cols-3 gap-3">
            <StatCard label="Friends referred" value={data?.referred_count ?? 0} testid="stat-referred" />
            <StatCard label="Rewards earned" value={(data?.personal_codes || []).filter(c => c.issued_by_referral?.kind === 'reward').length} testid="stat-rewards" />
            <StatCard label="Total codes" value={(data?.personal_codes || []).length} testid="stat-codes" />
          </div>

          {link && (
            <div className="mt-5 pt-5 border-t border-white/15">
              <div className="text-[10px] uppercase tracking-widest font-semibold text-white/60 mb-2">Ready-made invite message</div>
              <div className="bg-white/10 border border-white/20 rounded-lg p-3 text-xs text-white/90 leading-relaxed mb-2" data-testid="invite-message-preview">
                Buy Amazon, Flipkart, Netflix & Steam gift cards with crypto on TokenGate. Use my code <span className="font-semibold text-white">{data?.referral_code}</span> for 10% off. → {link}
              </div>
              <Button data-testid="copy-invite-message-btn"
                onClick={() => copy(`Buy Amazon, Flipkart, Netflix & Steam gift cards with crypto on TokenGate. Use my code ${data?.referral_code} for 10% off. → ${link}`, 'invite')}
                className="w-full bg-white text-[#8247E5] hover:bg-white/90 h-10 gap-2 font-semibold">
                {copiedKey === 'invite' ? <><Check className="w-4 h-4" /> Copied. Paste anywhere!</> : <><Copy className="w-4 h-4" /> Copy invite message</>}
              </Button>
            </div>
          )}

          <div className="mt-5 pt-5 border-t border-white/15">
            <div className="text-[10px] uppercase tracking-widest font-semibold text-white/60 mb-2">
              {link ? 'Or share in one tap' : 'Share (set your code above to enable)'}
            </div>
            <div className={`flex flex-wrap gap-2 ${link ? '' : 'opacity-40 pointer-events-none'}`}>
              <ShareBtn testid="share-whatsapp" href={link ? `https://wa.me/?text=${encodeURIComponent(`Hey! I've been buying Amazon, Netflix, Steam & more with crypto on TokenGate. Sign up with my link and we both get 10% off: ${link}`)}` : '#'} icon={MessageCircle} label="WhatsApp" bg="#25D366" />
              <ShareBtn testid="share-telegram" href={link ? `https://t.me/share/url?url=${encodeURIComponent(link)}&text=${encodeURIComponent('Try TokenGate. Buy gift cards with crypto. Sign up with my link and we both get 10% off.')}` : '#'} icon={Send} label="Telegram" bg="#0088CC" />
              <ShareBtn testid="share-twitter" href={link ? `https://twitter.com/intent/tweet?text=${encodeURIComponent(`Just discovered @TokenGate. Turn your crypto into Amazon, Netflix, Steam & more. Sign up with my link and we both get 10% off ⚡`)}&url=${encodeURIComponent(link)}` : '#'} icon={Twitter} label="Twitter / X" bg="#000000" />
              <ShareBtn testid="share-sms" href={link ? `sms:?body=${encodeURIComponent(`Sign up for TokenGate with my link and we both get 10% off: ${link}`)}` : '#'} icon={Mail} label="SMS" bg="#5856D6" />
              {typeof navigator !== 'undefined' && navigator.share && link && (
                <button data-testid="share-native"
                  onClick={() => navigator.share({ title: 'TokenGate. 10% off with my referral', text: 'Buy gift cards with crypto. Sign up with my link and we both get 10% off.', url: link }).catch(() => {})}
                  className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-white font-semibold text-xs bg-white/15 hover:bg-white/25 backdrop-blur transition-colors">
                  <Share2 className="w-3.5 h-3.5" /> More…
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-neutral-200 p-6">
        <h3 className="font-semibold text-lg mb-1 flex items-center gap-2"><Tag className="w-4 h-4 text-[#8247E5]" /> Your discount codes</h3>
        <p className="text-xs text-neutral-500 mb-4">Personal codes issued to you: welcome bonuses and referral rewards. Paste them into the Promo Code field at checkout.</p>
        {usableCodes.length === 0 && usedCodes.length === 0 ? (
          <div className="text-center py-8 text-sm text-neutral-500">
            No codes yet. Share your referral link. When a friend completes their first order, you'll earn a 10% off code here.
          </div>
        ) : (
          <div className="space-y-2">
            {usableCodes.map(c => <CodeRow key={c.code} c={c} onCopy={copy} copiedKey={copiedKey} active />)}
            {usedCodes.map(c => <CodeRow key={c.code} c={c} onCopy={copy} copiedKey={copiedKey} active={false} />)}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReferralsTab;
